import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import ProcedureValidationErrorInfoButton from 'procedures/design/buttons/ProcedureValidationErrorInfoButton';
import {DragAndDropStepItemProperties} from 'procedures/design/step-config/useDragDropSidebar';
import {ProcedureValidationObjectType} from 'procedures/design/validation/ProcedureValidationTypes';
import {multiLineEllipsisText} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_POWDER_LIGHT, GRAY_LIGHT, GRAY_LIGHTER, GRAY_SEMIDARK, WHITE} from 'ui-library/Colors';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import OverflowMenu, {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import {isDownKey, isEnterKey, isUpKey} from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';
import {NON_BREAKING_SPACE} from 'util/UnicodeSymbols';

interface ProcedureStepsListItemProps {
  name: string;
  stepCid?: string; // Only populated in config
  isSelected?: boolean;
  onClick: () => void;
  onDoubleClick?: () => void;
  nameClassName?: string;
  icon?: React.ReactNode | null; // `| null` here as note for explicit typing, not technically necessary
  overflowMenuItems?: Array<OverflowMenuOption>;
  // The drag and drop attributes and event handlers are created by the `useDragDropSidebar` hook
  dragAndDropProperties?: DragAndDropStepItemProperties;
}

// This default set of D&D properties will ensure a step is not included in any D&D operations
const INERT_DRAG_AND_DROP_PROPERTIES: DragAndDropStepItemProperties = {
  draggable: false,
  isBeingDragged: false,
  isBeingDraggedOver: false,
  eventHandlers: {},
};

function DragStepIcon(): JSX.Element {
  return <BenchlingIcon className={css(styles.dragIcon)} icon="drag-handle" />;
}

export default function ProcedureStepsListItem({
  name,
  stepCid,
  isSelected,
  onClick,
  onDoubleClick,
  nameClassName,
  icon,
  overflowMenuItems,
  dragAndDropProperties = INERT_DRAG_AND_DROP_PROPERTIES,
}: ProcedureStepsListItemProps): JSX.Element {
  const [isHovered, hoverProps] = useHoverState();
  const handleKeyDown = (e: ElementKeyboardEvent): void => {
    const activeElement = document.activeElement;
    if (isEnterKey(e)) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    } else if (isDownKey(e)) {
      e.preventDefault();
      e.stopPropagation();
      (activeElement?.nextSibling as HTMLElement)?.focus();
    } else if (isUpKey(e)) {
      e.preventDefault();
      e.stopPropagation();
      (activeElement?.previousSibling as HTMLElement)?.focus();
    }
  };

  return (
    <div
      draggable={dragAndDropProperties.draggable}
      className={css(
        styles.container,
        isSelected && styles.selectedContainer,
        dragAndDropProperties.isBeingDragged && styles.shadowOfElementBeingDragged,
        dragAndDropProperties.isBeingDraggedOver && styles.elementBeingHoveredOver
      )}
      tabIndex={0}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onKeyDown={handleKeyDown}
      role="button"
      {...dragAndDropProperties.eventHandlers}
      {...hoverProps}
      {...getTestProps({
        component: 'ProcedureStepsListItem',
        element: 'container',
        key: isSelected ? 'selected' : undefined,
      })}
    >
      {stepCid != null && (
        <div className={css(styles.errorIconWrapper)}>
          <ProcedureValidationErrorInfoButton
            primaryObject={{id: stepCid, objectType: ProcedureValidationObjectType.STEP}}
          />
        </div>
      )}
      {isHovered && dragAndDropProperties.draggable ? <DragStepIcon /> : icon}
      <div
        className={classNames(
          'u-lr-split',
          css(styles.nameContainer, dragAndDropProperties.draggable && styles.leaveRoomForDragIcon)
        )}
        {...getTestProps({component: 'ProcedureStepsListItem', element: 'name'})}
      >
        <span className={classNames(css(styles.name, isSelected && styles.selectedName), nameClassName)}>
          {name}
        </span>
        <span>
          {overflowMenuItems != null && (
            <OverflowMenu
              options={overflowMenuItems}
              buttonProps={{
                buttonStyle: ButtonStyle.TERTIARY_WHITE_TRANSPARENT,
                className: css(
                  styles.overflowMenuButton,
                  (isHovered || isSelected) && styles.overflowMenuButtonVisible
                ),
                dataTestAttrs: {component: 'ProcedureStepsListItem', element: 'overflowButton'},
              }}
              isIconHorizontal={false}
              {...getTestProps({component: 'ProcedureStepsListItem', element: 'overflow-menu'})}
            />
          )}
        </span>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: '40px',
    background: WHITE,
    borderTop: `1px solid ${GRAY_LIGHT}`,
    verticalAlign: 'middle',
    cursor: 'pointer',
    position: 'relative',
    ':hover': {
      background: GRAY_LIGHTER,
    },
  },
  leaveRoomForDragIcon: {
    paddingLeft: '12px',
  },
  dragIcon: {
    position: 'absolute',
    left: '8px',
    color: GRAY_SEMIDARK,
  },
  selectedContainer: {
    background: BLUE_POWDER_LIGHT,
    ':hover': {
      background: BLUE_POWDER_LIGHT,
    },
  },
  errorIconWrapper: {
    paddingLeft: '12px',
  },
  nameContainer: {
    width: '100%',
    padding: '0 8px',
  },
  name: {
    fontSize: '13px',
    ...multiLineEllipsisText(2),
  },
  selectedName: {
    fontWeight: 'bold',
  },
  overflowMenuButton: {
    border: 'none',
    color: GRAY_SEMIDARK,
    // Rather than truly hiding the menu on hover (or even suppressing rendering it at all),
    // We simply make it transparent. This allows the menu to function properly even as the user
    // interacts with it (i.e., as they un-hover the step item)
    opacity: 0,
  },
  overflowMenuButtonVisible: {
    opacity: 1,
  },
  shadowOfElementBeingDragged: {
    background: Color.GRAY_100,
    opacity: 1,
  },
  elementBeingHoveredOver: {
    '::before': {
      content: `"${NON_BREAKING_SPACE}"`,
      lineHeight: '1px',
      display: 'block',
      width: '100%',
      position: 'absolute',
      top: 0,
      borderTop: `3px solid ${Color.BLUE_450}`,
    },
  },
});
